import { useEffect, useMemo, useState } from 'react';
import * as TagManager from 'react-gtm-module';
import { useAuth } from '../auth/AuthProvider';

export interface DataLayerEvent {
  event: string;
  [key: string]: unknown;
}

export function initGTM(gtmId: string): void {
  if (!gtmId) return;
  TagManager.initialize({ gtmId });
}

export function pushEvent(payload: DataLayerEvent): void {
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(payload);
}

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const INTERNAL_DOMAINS = ['broadlume.com', 'cyncly.com', 'yopmail.com', 'mobile-marketing.com'];

export const getUserCategory = (email: string | undefined): 'internal' | 'external' => {
  if (!email) return 'external';
  const domain = email.split('@')[1] ?? '';
  return INTERNAL_DOMAINS.some((d) => domain === d) ? 'internal' : 'external';
};

export const useTrackingContext = (): {
  hashedEmail: string | undefined;
  userCategory: 'internal' | 'external';
} => {
  const auth = useAuth();
  const [hashedEmail, setHashedEmail] = useState<string | undefined>(undefined);
  const email = auth?.user?.attributes?.email;

  useEffect(() => {
    if (!email) {
      setHashedEmail(undefined);
      return;
    }

    // Clear immediately so no tracking fires with the previous user's hash
    // during the async window before the new digest resolves.
    setHashedEmail(undefined);

    let cancelled = false;
    crypto.subtle.digest('SHA-256', new TextEncoder().encode(email)).then((buf) => {
      if (cancelled) return;
      setHashedEmail(
        Array.from(new Uint8Array(buf))
          .map((b) => b.toString(16).padStart(2, '0'))
          .join(''),
      );
    });

    return () => {
      cancelled = true;
    };
  }, [email]);

  const userCategory = useMemo(() => getUserCategory(email), [email]);

  return { hashedEmail, userCategory };
};
